import React, { useEffect, useRef, useState } from 'react';
import FunctionMeshHero from '../../components/FunctionMeshHero';

export function CTASection() {
  const [isVisible, setIsVisible] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} id="contact" className="relative py-24 md:py-36 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        {/* Wider than the default 40, with lines and beads at the same density, so the mesh
            reaches the edges of very wide screens too. */}
        <FunctionMeshHero
          background="transparent"
          width={72}
          xLines={216}
          lineColor={isDark ? '#e8e4dc' : '#1a1a1a'}
          rowOpacity={isDark ? 0.12 : 0.15}
          colOpacity={isDark ? 0.08 : 0.1}
          speed={0.35}
          amplitude={1.2}
          freqX={0.16}
          freqZ={0.5}
          ridgeHeight={2.5}
          ridgeDrift={0.28}
          backLift={1.1}
          meshOffsetY={-2.0}
          cameraY={5.0}
          cameraZ={22}
          lookY={1.4}
          fov={31}
          signalDots={{
            count: 27,
            size: 0.15,
            sizeVariation: 0.35,
            speed: 0.8,
            speedVariation: 0.4,
            opacity: 0.9,
            moveX: true,
            moveZ: true,
            reverseChance: 0.5,
            dipRadius: 1.0,
            dipDepth: 0.2,
            colors: ['#4ADE80', '#E88BD5', '#F5A623'],
          }}
        />
      </div>
      
      <div className="relative z-10 max-w-wide mx-auto px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center">
          <div
            className={`flex items-center justify-center gap-2 mb-4 opacity-0 ${isVisible ? 'animate-fade-up' : ''}`}
            style={{ animationDelay: '0.05s' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-foreground/80" />
            <span className="text-section-eyebrow text-foreground-eyebrow">
              Start in the playground
            </span>
          </div>
          <h2
            className={`font-display text-heading md:text-display-sm text-foreground opacity-0 ${isVisible ? 'animate-fade-up' : ''}`}
            style={{ animationDelay: '0.15s' }}
          >
            Bring your hardest everyday decisions.
          </h2>
          <p
            className={`mt-6 font-body text-body text-foreground-tertiary opacity-0 ${isVisible ? 'animate-fade-up' : ''}`}
            style={{ animationDelay: '0.25s' }}
          >
            Write the question, try it on a real ticket, then call it from your code.
          </p>
          <div
            className={`mt-10 flex flex-wrap justify-center gap-4 opacity-0 ${isVisible ? 'animate-fade-up' : ''}`}
            style={{ animationDelay: '0.35s' }}
          >
            <a
              href="https://console.sqwish.ai/#playground"
              target="_blank"
              rel="noopener noreferrer"
              className="text-button font-body bg-foreground text-background px-6 py-3 rounded-full hover:bg-foreground/70 hover:shadow-lg transition-all duration-300"
            >
              Try the playground
            </a>
            <a
              href="https://console.sqwish.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-button font-body border border-foreground/30 text-foreground/70 px-6 py-3 rounded-full hover:border-foreground hover:text-foreground hover:bg-foreground/10 hover:shadow-md transition-all duration-300"
            >
              Open the console
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CTASection;
